/**
 * @file RestorePODialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-09-29
 * @description Restoring an archived purchase order. It asks rather than
 *              assumes (Cam, 2026-09-29): archiving took the PO off its
 *              customer order lines, and since then a line may have been put
 *              on another PO or removed. So this reports each line — free, on
 *              another PO, or gone — and offers to put the PO back on the free
 *              ones, or to restore it on its own (for its history, or to deal
 *              with separately).
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { poStatusLabel } from '../utils/poStatus';
import { formatQuantity } from '../utils/numberFormatters';
import './ArchivePOsFlow.css';

const STATE_COPY = {
  free: { icon: 'check_circle', text: 'free — can go back on' },
  on_other_po: { icon: 'link_off', text: 'now on' },
  gone: { icon: 'block', text: 'no longer on the order' }
};

function RestoreBody({ check, error, busy }) {
  useSlideOutButton([!!check, busy]);
  if (!check) return error ? <div className="archive-pos-error">{error}</div> : <LoadingSpinner />;
  return (
    <div className="archive-pos-body">
      <p className="archive-pos-intro">
        {check.po.number} goes back to <strong>{poStatusLabel(check.from_status || 'Draft')}</strong>
        {check.from_status ? '' : ' (its status before archiving was not recorded)'}.
        {check.order && <> It was raised for {check.order.order_number}{check.order.is_archived ? ', which is itself archived' : ''}.</>}
      </p>
      {error && <div className="archive-pos-error">{error}</div>}
      {check.lines.length > 0 ? (
        <div className="archive-pos-list">
          {check.lines.map(l => {
            const c = STATE_COPY[l.state];
            return (
              <div key={l.id} className={`restore-line-row ${l.state}`}>
                <span className="material-symbols-rounded">{c.icon}</span>
                <span className="restore-line-product">{l.product || `Line ${l.id}`}{l.quantity ? ` × ${formatQuantity(l.quantity)}` : ''}</span>
                <span className="restore-line-state">
                  {c.text}{l.state === 'on_other_po' && l.other_po_number ? ` ${l.other_po_number}` : ''}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="archive-pos-intro">
          {check.recorded ? 'It was on no order lines when it was archived.' : 'Which order lines it was on was not recorded, so it can only be restored on its own.'}
        </p>
      )}
    </div>
  );
}

function RestorePODialog({ po, onClose, onRestored }) {
  const [check, setCheck] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/purchase-orders/db/${po.dbId}/restore-check`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!ok) throw new Error(d.error || 'Could not check this PO'); setCheck(d); })
      .catch(e => setError(e.message));
  }, [po.dbId]);

  const restore = async (relink) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/purchase-orders/db/${po.dbId}/restore`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relink })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not restore');
      onRestored?.(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const free = check?.free_count || 0;
  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title={`Restore ${po.purchaseOrderNumber}`}
      size="md"
      className="archive-pos-flow"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={() => restore(false)} disabled={!check || busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">unarchive</span></div>
              <span className="btn-label">Restore on its own</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={() => restore(true)} disabled={!check || busy || !free}
              title={free ? '' : 'None of its old lines are free'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">link</span></div>
              <span className="btn-label">{`Restore and re-link ${free} line${free === 1 ? '' : 's'}`}</span>
            </button>
          </div>
        </div>
      }
    >
      <RestoreBody check={check} error={error} busy={busy} />
    </Modal>
  );
}

export default RestorePODialog;
